import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import { useChangeOrganisationOwner } from "../../hooks/useOrganisations";
import { useUsers } from "../../hooks/useUsers";
import { useSnackbar } from "../../contexts/SnackbarContext";

const ChangeOrganisationOwnerButton = ({ organisationId, currentOwnerId }) => {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const { showSuccess, showError } = useSnackbar();

  const { data: users, isLoading: usersLoading } = useUsers();
  const changeOwnerMutation = useChangeOrganisationOwner();

  const handleSubmit = () => {
    if (!selectedUserId) {
      showError("Please select a new owner");
      return;
    }

    changeOwnerMutation.mutate(
      {
        organisationId: parseInt(organisationId),
        ownerId: parseInt(selectedUserId),
      },
      {
        onSuccess: () => {
          showSuccess("Organisation owner changed successfully");
          handleClose();
        },
        onError: (error) => {
          showError(
            `Failed to change organisation owner: ${
              error.response?.data?.detail || error.message
            }`
          );
        },
      }
    );
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId("");
  };

  // Filter out current owner from the list
  const availableUsers =
    users?.filter((user) => user.id !== currentOwnerId) || [];

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PersonAdd />}
        onClick={() => setOpen(true)}
        size="small"
      >
        Change Owner
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Change Organisation Owner</DialogTitle>
        <DialogContent>
          {usersLoading ? (
            <CircularProgress />
          ) : (
            <FormControl fullWidth margin="normal">
              <InputLabel>Select New Owner</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label="Select New Owner"
              >
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {changeOwnerMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to change owner: {changeOwnerMutation.error?.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !selectedUserId || changeOwnerMutation.isPending || usersLoading
            }
          >
            {changeOwnerMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Change Owner"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangeOrganisationOwnerButton;
